import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useLocation} from "react-router-dom";
import {OrderModel, OrderStatus} from "../../../Api/Models/order.model";
import {stockService} from "../../../Api/Services/StockService";
import {useLoading} from "../../../Context/LoadingProvider";
import StockManagementNavigation from "../../../Components/StockManagementNavigation";
import {CalendarIcon} from "../../../Components/Icons/CalendarIcon";
import {BoxIcon} from "../../../Components/Icons/BoxIcon";
import MySelect from "../../../Components/MySelect";
import {HomeIcon} from "../../../Components/Icons/HomeIcon";

interface StockOrderListProps {}

interface OrderStatusTextColor {
  text: string;
  color: string;
}

const StockOrderList: React.FunctionComponent<StockOrderListProps> = () => {
  const [orders, setOrders] = useState<OrderModel[]>([]);
  const { startLoading, finnishLoading } = useLoading();
  const location = useLocation(); // Hozzáférünk a location state-hez
  const { selectedFilter: initialSelectedFilter } = location.state || {}
  const [selectedFilter, setSelectedFiler] = useState<number>(initialSelectedFilter ?? -2);
  const [page, setPage] = useState<number>(1);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  //const lazyLoadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const divRef = useRef<HTMLDivElement | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const getOrderAndSync = async () => {
    if (isFetching) return; // védelem
    setIsFetching(true);
    startLoading("get-order-list");
    try {
      const newOrders = await stockService.listStockOrders(page, selectedFilter);
      if (page === 1) {
        setOrders(newOrders);
      } else {
        setOrders((prev) =>
            [...prev, ...newOrders].filter((value, index, self) => self.findIndex(v => v.id === value.id) === index)
        );
      }
    } finally {
      finnishLoading("get-order-list");
      setIsFetching(false);
    }
  };


  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      getOrderAndSync();
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
//eslint-disable-next-line
  }, [page, selectedFilter]);


  useEffect(() => {
    const handleScroll = () => {
      if (
          window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10 &&
          !isFetching
      ) {
        setPage((prevPage) => prevPage + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isFetching]);


  useEffect(() => {
    setOrders([]);
    setPage(1);
//eslint-disable-next-line
  }, [selectedFilter]);

  const getOrderStatusText = (
      orderStatus: OrderStatus
  ): OrderStatusTextColor => {
    switch (orderStatus) {
      case OrderStatus.PENDING:
        return { text: t("Pending"), color: "pending" };

      case OrderStatus.APPROVED:
        return { text: t("Approved"), color: "approved" };

      case OrderStatus.APPROVED_LVL1:
        return { text: t("Approved LVL1"), color: "approved" };

      case OrderStatus.DECLINED:
        return { text: t("Declined"), color: "declined" };

      case OrderStatus.IN_TRANSIT:
        return { text: t("In transit"), color: "in_transit" };

      case OrderStatus.DELIVERED:
        return { text: t("Delivered"), color: "delivered" };

      case OrderStatus.INBOUNDED:
        return { text: t("Inbounded"), color: "inbounded" };
      default:
        return {
          text: "",
          color: "",
        };
    }
  };

  const getFilterOptions = () => {
    const statuses = [
      { value: -2, label: "All" },
      { value: OrderStatus.APPROVED },
      { value: OrderStatus.PENDING },
      { value: OrderStatus.IN_TRANSIT },
      { value: OrderStatus.DELIVERED },
    ];

    return statuses.map(({ value, label }) => {
      if (label) {
        return { value, label };
      }

      const statusText = getOrderStatusText(value);

      return {
        value,
        label: (
            <>
              <div className="flex gap-2 items-center">
                <div className={`myselect__option-dot dot ${statusText.color}`}></div>
                {statusText.text}
              </div>
            </>
        ),
      };
    });
  };

  return (
      <div className="view-container background-white">
        <div className="view-content">

          <div className="navigationbar navigationbar--dark mb-6">
            <div className="navigationbar__left">
              <Link to="/menu" className="navigationbar__button">
                <HomeIcon />
              </Link>
            </div>
            <div className="navigationbar__center">
              <div className="navigationbar__title">{t("Stock Management")}</div>
            </div>
            <div className="navigationbar__right">
            </div>
          </div>

          <div className="mb-4">
            <StockManagementNavigation selectedIndex={1}/>
          </div>

          <div>
            {/*{
            <select
                className="input-field w-full mb-4"
                value={selectedFilter}
                onChange={(e) => setSelectedFiler(parseInt(e.target.value))}
            >
              <option value={-1}>{t("All")}</option>
              <option value={OrderStatus.APPROVED}>{t("Approved")}</option>
              <option value={OrderStatus.PENDING}>{t("Pending")}</option>
              <option value={OrderStatus.IN_TRANSIT}>{t("In Transit")}</option>
              <option value={OrderStatus.DELIVERED}>{t("Delivered")}</option>
            </select>
            }*/}

            <div className="mb-4">
              <MySelect
                  options={getFilterOptions()}
                  value={selectedFilter}
                  onChange={(value) => setSelectedFiler(parseInt(value))}
              />
            </div>

            <div className="card-list" ref={divRef}>
              {orders
                  .filter((order) =>
                      order.status !== OrderStatus.INBOUNDED &&
                      order.status !== OrderStatus.DECLINED &&
                      (
                          selectedFilter === -2 ||
                          (
                              selectedFilter === OrderStatus.APPROVED
                                  ? (order.status === OrderStatus.APPROVED || order.status === OrderStatus.APPROVED_LVL1)
                                  : order.status === selectedFilter
                          )
                      )
                  )
                  .map((order) => (
                      <Link
                          to={`/stock/orders/${order.id}`}
                          state={{ orderData: order, selectedFilter }}
                          className="order-list-card"
                          key={order.id}
                      >
                        <div className="flex">
                          <div className="order-list-card__left flex">
                            <div className="relative">
                              <div className="order-list-card__image">
                                <img
                                    src={order.products[0].image ?? ""}
                                    style={{ width: "50px" }}
                                    alt="Product"
                                />
                              </div>

                              {order.products[0].variationName && (
                                  <div className="order-list-card__variation-name">
                                    {order.products[0].variationName ?? ""}
                                  </div>
                              )}

                            </div>
                            <div>
                              <div className="stock-list-card__title">
                                {order.products[0].name ?? ""}
                              </div>
                            </div>
                          </div>

                          <div className="order-list-card__right">
                            <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  whiteSpace: "nowrap",
                                  minWidth: "fit-content",
                                }}
                            >
                              <div
                                  className={
                                      "order-status " + getOrderStatusText(order.status).color
                                  }
                              >
                                {getOrderStatusText(order.status).text}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2 mt-2">
                          <div className="order-list-card__info">
                            <CalendarIcon />
                            {order.deliveryDate ? order.deliveryDate : order.orderDate}
                          </div>

                          <div className="order-list-card__info">
                            <BoxIcon /> {order.products[0]?.quantity}&nbsp;{t("pcs")}
                          </div>


                          {order.products[0]?.receivedQuantity ? (
                              <div className="order-list-card__info">
                                {t("Received")}:&nbsp;
                                {order.products[0].receivedQuantity}&nbsp;{t("pcs")}
                              </div>
                          ) : null}

                        </div>
                      </Link>
                  ))}
            </div>



          </div>
        </div>
      </div>
  );
};

export default StockOrderList;
